/**
 * DmlTargetCard — shared mutating-intent target primitive for DML statements.
 * Single source of the red DELETE treatment (extracted verbatim from the
 * former `DeleteTargetCard` in `render/delete/DeleteRenderer.tsx` — a
 * PRESERVE-LIST praise item, `dml-delete-mssql-compact`) and now also drives
 * the amber UPDATE treatment, so "this statement mutates rows" reads
 * consistently across DeleteRenderer, UpdateRenderer, CompactDeleteSections
 * and CompactUpdateSections. DELETE ranks above UPDATE (red vs. amber) —
 * both are mutating, only one is irreversible-by-row-loss.
 *
 * `variant='full'` renders the target row used by the full-view renderers:
 * one line — intent label, target and the renderer's `targetAction` (the
 * "Change table" picker) — so the statement's target does not take a tall
 * card of its own. (There is no "+ Add table" here: adding a second target
 * table is not implemented, and a permanently disabled button was noise — user
 * ruling 2026-09-25. There is no × either: removing the target could only
 * leave the statement without one, so it became "Change table".)
 * `variant='banner'` renders the one-line compact-popover form (icon + verb +
 * target) used by both `Compact*Sections`.
 */

import type { ComponentType, ReactNode } from 'react';
import { SquarePen, Trash2 } from 'lucide-react';

export type DmlIntent = 'delete' | 'update';

export interface DmlTargetCardProps {
  readonly intent: DmlIntent;
  readonly variant: 'full' | 'banner';
  readonly targetText: string;
  readonly children?: ReactNode;
  /**
   * Optional slot rendered under the target line (in the banner variant, as a
   * sibling BELOW the banner line so it never pollutes the banner's testid
   * textContent; in the full variant, inside the card). This is the seam the
   * compact DML sections use to surface the §A2 scope-warning line + §A4
   * blast-radius chip — a pure prop-pass once prim #1 lands.
   */
  readonly footer?: ReactNode;
  /** Overrides the default `vqe-[compact-]{intent}-target` testid convention. */
  readonly testId?: string;
  /** Full variant: the action beside the target (the "Change table" picker). */
  readonly targetAction?: ReactNode;
}

interface IntentStyle {
  readonly icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  /** Full-card header label. */
  readonly title: string;
  /** Compact-banner verb prefix. */
  readonly verb: string;
  readonly cardClass: string;
  readonly headerClass: string;
  readonly targetTextClass: string;
  readonly bannerClass: string;
}

const INTENT_STYLE: Record<DmlIntent, IntentStyle> = {
  delete: {
    icon: Trash2,
    title: 'Delete from',
    verb: 'DELETE FROM',
    cardClass: 'rounded-lg border border-destructive/60 bg-destructive/5 px-3 py-2 shadow-sm',
    headerClass:
      'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-destructive',
    targetTextClass:
      'rounded-md border border-destructive/40 bg-background/40 px-2 py-1 font-mono text-xs',
    bannerClass:
      'flex items-center gap-1.5 rounded-md border border-destructive/60 bg-destructive/5 px-2 py-1 font-mono text-[11px] text-destructive',
  },
  update: {
    icon: SquarePen,
    title: 'UPDATE',
    verb: 'UPDATE',
    cardClass: 'rounded-lg border border-amber-500/60 bg-amber-500/5 px-3 py-2 shadow-sm',
    headerClass:
      'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400',
    targetTextClass:
      'rounded-md border border-amber-500/40 bg-background/40 px-2 py-1 font-mono text-xs',
    bannerClass:
      'flex items-center gap-1.5 rounded-md border border-amber-500/60 bg-amber-500/5 px-2 py-1 font-mono text-[11px] text-amber-700 dark:text-amber-400',
  },
};

export function DmlTargetCard({
  intent,
  variant,
  targetText,
  children,
  footer,
  testId,
  targetAction,
}: DmlTargetCardProps) {
  const style = INTENT_STYLE[intent];
  const Icon = style.icon;
  const resolvedTestId = testId ?? `vqe-${variant === 'banner' ? 'compact-' : ''}${intent}-target`;

  if (variant === 'banner') {
    const line = (
      <div
        className={style.bannerClass}
        data-testid={resolvedTestId}
        data-destructive-intent={intent}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
        {style.verb} {targetText}
        {children}
      </div>
    );
    if (!footer) return line;
    // Footer is a SIBLING below the banner line so the banner's testid element
    // keeps its exact `VERB target` textContent (e2e relies on it).
    return (
      <div className="flex flex-col gap-1">
        {line}
        {footer}
      </div>
    );
  }

  return (
    <section
      className={style.cardClass}
      data-testid={resolvedTestId}
      data-destructive-intent={intent}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={style.headerClass} data-testid={`${resolvedTestId}-title`}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {style.title}
        </span>
        <span className={style.targetTextClass} data-testid={`${resolvedTestId}-text`}>
          {targetText}
        </span>
        {targetAction}
      </div>
      {children}
      {footer}
    </section>
  );
}
